import i18next from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { BreadcrumbItem, BreadcrumbLayout } from '../../../../utils/breadcrumbs'
import { ButtonGhost, ButtonPrimary } from '../../../../utils/buttons'
import { DataLayer } from '../../../../utils/datalayer'
import {
  DietaryWishes,
  allergenIconId,
  dietaryWishes,
  dietaryWishesIconId,
  notPescotarian,
  notVegan,
  notVegetarian,
} from '../../../../utils/dietaryWishesAndAllergens'

import { Ingredients, IngredientsList } from '../../../../utils/ingredients'
import { blockInviewHandler } from '../../../../utils/inview'
import { ComponentDidMount } from '../../../../utils/lifecycleMethods'
import { componentDetailPath, componentEditPath } from '../../../../utils/pages'
import { SVGIcon } from '../../../../utils/svgicon'
import { Toast } from '../../../../utils/toast'
import { TooltipLayout } from '../../../../utils/tooltip'
import { slugify } from '../../../../utils/utils'
import { PictureLayout } from '../../image/imageLayout'
import { ImageData } from '../../image/imageState'
import { SponsoredLayout } from '../../sponsor/sponsorLayout'
import { SponsorData } from '../../sponsor/sponsorState'
import { AllergenData, AllergensData, AmountConverterState } from './componentDetailState'
import { AmountConverter, AmountConverterInputType } from './amountConverterLayout'

export const ComponentDetailLayout = (props: {
  showToast: boolean
  onCloseToast: () => void
  id: number
  name: string
  introduction: string
  preparation: string
  servingSuggestions: string
  allergens: AllergensData
  ingredients: Ingredients
  brand: SponsorData
  image: ImageData
  isSaved: boolean | 'unknown'
  isOwnComponent: boolean
  parentComponent?: { id: number; name: string }
  isDraft: boolean
  onSave: () => void
  onEditWhenLoggedOut: () => void
  isLoggedIn: boolean
  hasPaywall: boolean
  breadcrumbs?: List<BreadcrumbItem>
  amountConverterState: AmountConverterState
  onChangeAmountConverterMultiplier: (multiplier: number | undefined, inputText: string, inputType: AmountConverterInputType) => void
}) => {
  const {
    showToast,
    id,
    name,
    introduction,
    preparation,
    servingSuggestions,
    allergens,
    ingredients,
    brand,
    image,
    breadcrumbs,
    isLoggedIn,
    hasPaywall,
    isSaved,
    isOwnComponent,
    parentComponent,
    isDraft,
    onCloseToast,
    onSave,
    onEditWhenLoggedOut,
    amountConverterState,
    onChangeAmountConverterMultiplier
  } = props

  const allergensAndDietaryWishesTitles = allergens.map(
    (allergen) => allergen.title
  )
  const isPescotarian =
    !allergensAndDietaryWishesTitles.includes(notPescotarian)
  const isVegetarian =
    isPescotarian && !allergensAndDietaryWishesTitles.includes(notVegetarian)
  const isVegan =
    isVegetarian && !allergensAndDietaryWishesTitles.includes(notVegan)

  const actualAllergens = allergens.filter(
    (allergen) => !dietaryWishes.includes(allergen.title)
  )

  const hasDietaryWishes = isPescotarian || isVegetarian || isVegan
  const hasAllergens = actualAllergens.length > 0

  const converterMultiplierMinimum = 0
  const converterMultiplierMaximum = 99


  const calculateIngredientAmount = (amount: string, multiplier: number | undefined) => {
    if(!amount)
      return amount;

    return isNaN(+amount) ? amount : roundTo2Decimals(+amount * (multiplier ? multiplier : 1)).toString();
  }

  const roundTo2Decimals = (value: number) => {
    return Math.round(value * 100) / 100
  }

  return (
    <>
      <ComponentDidMount
        onMount={() => {
          if (brand.Brand) DataLayer.loadSponsoredPage(brand.Brand)
        }}
      />
      {/* 'Saved' toast after saving own component */}
      <div className="main__toast container--m gutter--m">
        <Toast
          isVisible={showToast == true ? true : false}
          iconId="checkmark"
          toastCopy={i18next.t('common:saved')}
          onClose={onCloseToast}
        />
      </div>

      <InView
        as="section"
        threshold={0}
        triggerOnce={true}
        onChange={(isInView, entry) =>
          blockInviewHandler(isInView, entry.target)
        }
        aria-hidden={hasPaywall}
        className={`block block--component-detail${
          isOwnComponent || isDraft ? ' component-detail--shadow' : ''
        }${isDraft ? ' component-detail--shadow-draft' : ''}${
          hasPaywall ? ' component-detail--paywall' : ''
        }`}
      >
        <div className="block__inner container--m gutter--m">
          <div className="block__content">
            <header className="block__header">
              {isDraft && (
                <div className="block__label">
                  <p className="label--m">
                    <SVGIcon iconId="concept" size={12} />
                    <span>{i18next.t('common:draft')}</span>
                  </p>
                </div>
              )}
              {isOwnComponent && !isDraft && (
                <div className="block__label">
                  <p className="label--m">
                    <SVGIcon iconId="user2" size={12} />
                    <span>{i18next.t('component:myComponent')}</span>
                  </p>
                </div>
              )}

              <h1
                className="block__title title--m"
                dangerouslySetInnerHTML={{ __html: name }}
              />

              {/* Breadcrumbs */}
              {breadcrumbs && <BreadcrumbLayout items={breadcrumbs} />}

              {/* Product placement */}
              {!isOwnComponent && !isDraft && (
                <SponsoredLayout
                  copy={i18next.t('component:sponsoredComponent')}
                  sponsorData={brand}
                  language={i18next.language}
                />
              )}

              {/* Intro */}
              <div className="block__intro text--styled">
                {introduction && (
                  <div dangerouslySetInnerHTML={{ __html: introduction }} />
                )}
              </div>
            </header>

            {/* Mobile image */}
            <div className="block__visual block__visual--mobile">
              <div className="block__visual-inner fit-object">
                {!isOwnComponent && !isDraft && (
                  <PictureLayout.Wrapper>
                    {/* On desktop, load empty.png */}
                    <PictureLayout.Source
                      media="(min-width: 48rem)"
                      srcSet={PictureLayout.PlaceholderSrc}
                    />
                    <PictureLayout.Img
                      image={image}
                      width="335"
                      height="268"
                      lazyload={false}
                      extraClasses="block__image"
                    />
                  </PictureLayout.Wrapper>
                )}
              </div>
            </div>


            {/* print image */}
            <div className="block__visual block__visual--mobile block__visual--print">
              <div className="block__visual-inner fit-object">
                {!isOwnComponent && !isDraft && (
                  <PictureLayout.Wrapper>
                    <PictureLayout.Img
                      image={image}
                      width="335"
                      height="268"
                      lazyload={false}
                      extraClasses="block__image"
                    />
                  </PictureLayout.Wrapper>
                )}
              </div>
            </div>

            {/* Save and Edit Button */}
            <div className="block__section block__actions">
              <SaveButton
                isSaved={isSaved}
                onSave={onSave}
                hasPaywall={hasPaywall}
                isLoggedIn={isLoggedIn}
              />
              <EditButton
                isOwnComponent={isOwnComponent || isDraft}
                id={id}
                hasPaywall={hasPaywall}
                isLoggedIn={isLoggedIn}
                onEditWhenLoggedOut={onEditWhenLoggedOut}
              />
            </div>

            {/* Allergens */}
            <div className="block__section">
              <h2 className="block__subtitle block__subtitle--tooltip title--xs">
                {i18next.t('component:allergensAndDietaryRequirements')}

                <TooltipLayout
                  id="allergens-disclaimer"
                  tooltipCopy={i18next.t('component:allergensTooltip')}
                >
                  <SVGIcon iconId="info-circle" size="20" />
                </TooltipLayout>
              </h2>

              {hasAllergens || hasDietaryWishes ? (
                <ul className="list--allergens">
                  {hasAllergens &&
                    actualAllergens.map((allergen) => (
                      <AllergenItemLayout kind="allergen" allergen={allergen} />
                    ))}
                  {hasDietaryWishes && (
                    <>
                      {isVegan ? (
                        <AllergenItemLayout
                          kind="dietaryWish"
                          dietaryWish="dierlijk-vegan"
                        />
                      ) : isVegetarian ? (
                        <AllergenItemLayout
                          kind="dietaryWish"
                          dietaryWish="vlees-vis-vogel-vegetarisch"
                        />
                      ) : (
                        isPescotarian && (
                          <AllergenItemLayout
                            kind="dietaryWish"
                            dietaryWish="landdieren-pescotarisch"
                          />
                        )
                      )}
                    </>
                  )}
                </ul>
              ) : (
                <p>{i18next.t('component:noAllergens')}</p>
              )}
            </div>

            {/* Ingredients */}
            {ingredients.length > 0 && (
              <div className="block__section">
                <h2 className="block__subtitle title--xs">
                  {i18next.t('component:ingredients')}
                </h2>
                {ingredients.length > 0 && (
                  <IngredientsList ingredients={ingredients.map(i => ({...i, amount: calculateIngredientAmount(i.amount, amountConverterState.multiplier)}))} />
                )}
                
                <AmountConverter
                  multiplierMinimum={0}
                  multiplierMaximum={99}
                  amountConverterState={amountConverterState}
                  onChangeMultiplier={onChangeAmountConverterMultiplier}
                />
              </div>
            )}

            {!hasPaywall && (
              <>
                {/* Preparation */}
                <div className="block__section">
                  <h2 className="block__subtitle title--xs">
                    {i18next.t('component:preparationMethod')}
                  </h2>
                  <div
                    className="text--styled"
                    dangerouslySetInnerHTML={{ __html: preparation }}
                  />
                </div>

                {/* Serving */}
                {servingSuggestions && (
                  <div className="block__section">
                    <h2 className="block__subtitle title--xs">
                      {i18next.t('component:servingSuggestions')}
                    </h2>
                    <div
                      className="text--styled"
                      dangerouslySetInnerHTML={{
                        __html: servingSuggestions,
                      }}
                    />
                  </div>
                )}

                {/* Save, Edit & Print button */}
                <footer
                  className="block__section block__actions"
                  aria-hidden="true"
                >
                  <SaveButton
                    isSaved={isSaved}
                    onSave={onSave}
                    hasPaywall={hasPaywall}
                    isLoggedIn={isLoggedIn}
                  />
                  <EditButton
                    isOwnComponent={isOwnComponent || isDraft}
                    id={id}
                    hasPaywall={hasPaywall}
                    isLoggedIn={isLoggedIn}
                    onEditWhenLoggedOut={onEditWhenLoggedOut}
                  />
                  <PrintButton />
                </footer>

                {/* Parent component URL for shadow component */}
                {parentComponent != undefined && (
                  <div className="block__section">
                    <p>
                      <span>{i18next.t('component:variantParentLabel')}</span>{' '}
                      <Link
                        to={componentDetailPath(
                          parentComponent.id,
                          parentComponent.name
                        )}
                        className="link--inverted"
                      >
                        {parentComponent.name}
                      </Link>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Desktop image */}
          <div className="block__visual block__visual--desktop hide--768px-down">
            <div className="block__visual-inner fit-object">
              {!isOwnComponent && !isDraft && (
                <PictureLayout.Wrapper>
                  {/* On mobile, load empty.png */}
                  <PictureLayout.Source
                    media="(max-width: 47.9rem)"
                    srcSet={PictureLayout.PlaceholderSrc}
                  />
                  <PictureLayout.Img
                    image={image}
                    width="510"
                    height="638"
                    extraClasses="block__image"
                    lazyload={false}
                  />
                </PictureLayout.Wrapper>
              )}
            </div>
          </div>
        </div>
      </InView>
    </>
  )
}

const AllergenItemLayout = (
  p:
    | { kind: 'allergen'; allergen: AllergenData }
    | { kind: 'dietaryWish'; dietaryWish: DietaryWishes }
) => (
  <li
    className={`list__item list__item--${
      p.kind == 'allergen' ? p.allergen.title : p.dietaryWish
    }`}
  >
    <svg className="list__icon" width="40" height="40" aria-hidden="true">
      <use
        xlinkHref={`#icon--${
          p.kind == 'allergen'
            ? allergenIconId(p.allergen.title)
            : dietaryWishesIconId(p.dietaryWish)
        }`}
      />
    </svg>
    <span className="list__label text--xs">
      {p.kind == 'allergen'
        ? p.allergen.translation
        : i18next.t(`component:${slugify(p.dietaryWish)}`)}
    </span>
  </li>
)

const SaveButton = (props: {
  isLoggedIn: boolean
  hasPaywall: boolean
  isSaved: boolean | 'unknown'
  onSave: () => void
}) => (
  <div className="block__button-wrapper">
    <ButtonPrimary
      kind="button"
      onClick={props.onSave}
      size="s"
      iconId={props.isSaved === true ? 'bookmark-full' : 'bookmark-ghost'}
      iconSize="20"
      label={i18next.t('common:add')}
      extraClasses="block__button"
      isDisabled={props.isLoggedIn && props.hasPaywall}
    />
  </div>
)

const EditButton = (props: {
  id: number
  isOwnComponent: boolean
  isLoggedIn: boolean
  onEditWhenLoggedOut: () => void
  hasPaywall: boolean
}) => (
  <div className="block__button-wrapper">
    <ButtonPrimary
      kind={props.isLoggedIn ? 'link' : 'button'}
      onClick={props.isLoggedIn ? undefined : props.onEditWhenLoggedOut}
      size="s"
      iconId="edit"
      iconSize="20"
      to={componentEditPath(props.id)}
      label={
        props.isOwnComponent === true
          ? i18next.t('common:edit')
          : i18next.t('componentEdit:makeVariantTitle')
      }
      extraClasses="block__button"
      isDisabled={props.isLoggedIn && props.hasPaywall}
    />
  </div>
)

const PrintButton = () => (
  <div className="block__button-wrapper">
    <ButtonGhost
      kind="button"
      onClick={() => window.print()}
      size="s"
      iconId="print"
      iconSize="20"
      label={i18next.t('common:print')}
      title={i18next.t('common:print')}
      extraClasses="block__button block__button--print"
    />
  </div>
)

